import type {
    PortalPlanBehavior, PortalPlanProgram, PortalPlanTarget,
    PortalPlanCustomField,
} from '@/api/portal';

// Read-only renderer for a treatment plan's full clinical content — behaviors,
// programs (with targets), and any practice-defined custom fields. Used in the
// parent portal in two places: the Review-and-sign dialog (pre-signature) and
// the Treatment Plan tab card (re-read any time). The server resolves custom
// fields into { label, type, value } rows so we don't need the defs catalog
// client-side. No PHI is surfaced here that the parent isn't already entitled
// to see — the portal endpoints scope rows by clientId.

interface Props {
    notes: string;
    behaviors: PortalPlanBehavior[];
    programs: PortalPlanProgram[];
    customFields: PortalPlanCustomField[];
}

export function PlanContent({ notes, behaviors, programs, customFields }: Props) {
    const hasAny = !!notes || behaviors.length > 0 || programs.length > 0
        || customFields.some((f) => !isEmptyValue(f.value));

    if (!hasAny) {
        return (
            <p className="text-sm text-text-subtle italic">
                No additional details on this plan yet.
            </p>
        );
    }

    return (
        <div className="space-y-4 text-sm">
            {notes && (
                <Section title="Plan details">
                    <p className="whitespace-pre-wrap text-text">{notes}</p>
                </Section>
            )}

            {behaviors.length > 0 && (
                <Section title={`Target behaviors (${behaviors.length})`}>
                    <ul className="divide-y divide-border border border-border rounded-md overflow-hidden">
                        {behaviors.map((b) => (
                            <li key={b.id} className="px-3 py-2">
                                <div className="font-medium text-text">{b.name}</div>
                                {b.behaviorType && (
                                    <div className="text-xs text-text-subtle mt-0.5">
                                        {b.behaviorType}
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                </Section>
            )}

            {programs.length > 0 && (
                <Section title={`Programs & goals (${programs.length})`}>
                    <div className="space-y-3">
                        {programs.map((p) => <ProgramBlock key={p.id} program={p} />)}
                    </div>
                </Section>
            )}

            {customFields.length > 0 && (
                <CustomFieldsSection fields={customFields} />
            )}
        </div>
    );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <div>
            <div className="text-xs font-medium text-text-subtle mb-1.5">{title}</div>
            {children}
        </div>
    );
}

function ProgramBlock({ program }: { program: PortalPlanProgram }) {
    const meta = [
        program.programDomain,
        program.teachingMethod,
    ].filter(Boolean).join(' · ');
    return (
        <div className="border border-border rounded-md overflow-hidden">
            <div className="px-3 py-2 bg-surface">
                <div className="font-medium text-text">{program.name}</div>
                {meta && <div className="text-xs text-text-subtle mt-0.5">{meta}</div>}
            </div>
            {program.targets.length > 0 && (
                <ul className="divide-y divide-border border-t border-border">
                    {program.targets.map((t) => <TargetRow key={t.id} target={t} />)}
                </ul>
            )}
        </div>
    );
}

function TargetRow({ target }: { target: PortalPlanTarget }) {
    const criterion = buildMasteryText(target);
    return (
        <li className="px-3 py-2">
            <div className="text-sm text-text">{target.name}</div>
            {target.description && (
                <div className="text-xs text-text-muted whitespace-pre-wrap mt-0.5">
                    {target.description}
                </div>
            )}
            {criterion && (
                <div className="text-[11px] text-text-subtle mt-1">{criterion}</div>
            )}
        </li>
    );
}

function buildMasteryText(t: PortalPlanTarget): string {
    const parts: string[] = [];
    if (t.masteryValue !== null) {
        const direction = t.masteryDirection === 'below' ? '≤' : '≥';
        const unit = t.dataType === 'percent' ? '%' : '';
        parts.push(`Mastery ${direction} ${t.masteryValue}${unit}`);
    }
    if (t.masterySessions) parts.push(`${t.masterySessions} sessions`);
    if (t.trials)          parts.push(`${t.trials} trials`);
    if (t.chainingMethod)  parts.push(t.chainingMethod);
    return parts.join(' · ');
}

function CustomFieldsSection({ fields }: { fields: PortalPlanCustomField[] }) {
    const visible = fields.filter((f) => !isEmptyValue(f.value));
    if (visible.length === 0) return null;
    return (
        <Section title="Additional details">
            <dl className="divide-y divide-border border border-border rounded-md overflow-hidden">
                {visible.map((f) => (
                    <div key={f.defId} className="px-3 py-2">
                        <dt className="text-xs text-text-subtle">{f.label}</dt>
                        <dd className="text-sm text-text mt-0.5">{formatCfValue(f)}</dd>
                    </div>
                ))}
            </dl>
        </Section>
    );
}

function isEmptyValue(v: unknown): boolean {
    if (v === null || v === undefined) return true;
    if (typeof v === 'string') return v.trim() === '';
    if (Array.isArray(v)) return v.length === 0;
    return false;
}

function formatCfValue(f: PortalPlanCustomField): React.ReactNode {
    const v = f.value;
    if (f.type === 'checkbox') return v === true ? 'Yes' : 'No';
    if (f.type === 'multiselect') {
        const arr = Array.isArray(v) ? v.map(String).filter(Boolean) : [];
        return arr.length ? arr.join(', ') : '—';
    }
    if (f.type === 'textarea' && typeof v === 'string') {
        return <span className="whitespace-pre-wrap">{v}</span>;
    }
    if (typeof v === 'string' || typeof v === 'number') return String(v);
    return '—';
}
